import type { fabric } from "fabric"
import Util from "@/common/util"
import Calc from "@/common/util/calculator"
import SignImage from "./objects/sign_image"
import SignText from "./objects/sign_text"
import Sign, { type ActiveObjectInfo } from "@/signomatic/app/sign"
import { SimpleEventDispatcher, SignalDispatcher } from "strongly-typed-events"
import { stripEmoji, loadImage, stripInvalid, defaultImageSize } from "@/signomatic/util"
import { correctFontSize } from "@/signomatic/app/sign_validator"
import { info, error } from "@/signomatic/util/logger"
import Fonts from "@/signomatic/util/fonts"
import { contourSize } from "./contour"
import { throttle } from "lodash-es"
import type SignHistory from "./sign_history"
import env from "@/common/env"
import ClientData from "@/common/client_data"
import type { Fontdata } from "@/signomatic/util/fonts"

export type SignObjecType = "text" | "image"

export type SignObject = SignImage | SignText

interface PositionResult {
  centerX: boolean
  centerY: boolean
  guidesX: number[]
  guidesY: number[]
}

export default class SignObjects {
  readonly onActiveChangedEvent = new SimpleEventDispatcher<SignObjectState | null>()
  readonly onActiveInfoChangedEvent = new SimpleEventDispatcher<ActiveObjectInfo | null>()
  readonly onGlyphsPresentEvent = new SimpleEventDispatcher<boolean>()
  readonly onEmojiUsedEvent = new SignalDispatcher()
  readonly onRotatingStartedEvent = new SignalDispatcher()
  readonly onRotatingStoppedEvent = new SignalDispatcher()
  readonly onObjectPositionChangedEvent = new SimpleEventDispatcher<number[]>()
  private readonly sign: Sign
  private readonly history: SignHistory
  private objects: SignObject[]
  private objectsToRestore = 0

  constructor(sign: Sign, history: SignHistory) {
    this.sign = sign
    this.history = history
    this.objects = []
  }

  get length() {
    return this.objects.length
  }

  get texts() {
    return this.sortByStateIndex(this.objects.filter(Sign.isTextObj)) as SignText[]
  }

  get images() {
    return this.sortByStateIndex(this.objects.filter(Sign.isImageObj)) as SignImage[]
  }

  get objectsRestored() {
    return this.objectsToRestore === 0
  }

  get active() {
    const obj = this.sign.activeObject

    if (this.isSignObject(obj)) {
      return obj
    }

    return null
  }

  private get state() {
    return this.sign.state
  }

  private get centerX() {
    return this.sign.centerX
  }

  private get centerY() {
    return this.sign.centerY
  }

  all() {
    return this.sortByStateIndex(this.objects)
  }

  allByZIndex() {
    return this.objects.sort((a, b) => a.state.zindex - b.state.zindex)
  }

  get(state: SignObjectState) {
    return this.objects.find(obj => Sign.sameObject(obj.state, state))
  }

  async create(type: SignObjecType, initialState?: Partial<SignTextState>) {
    let obj: SignObject | null = null

    if (type === "text") {
      obj = this.createText(initialState)
    } else if (type === "image") {
      obj = await this.createImage(initialState)
    }

    if (obj) {
      this.add(obj)
    }

    return obj
  }

  add(obj: SignObject) {
    obj.attachSign(this.sign)
    this.objects.push(obj)
    const objects = [...this.sign.state.objects, obj.state]
    this.sign.updateState({ objects }, true, true)
  }

  restoreObjectInstance(obj: SignObject) {
    obj.attachSign(this.sign)
    this.objects.push(obj)
  }

  remove(obj?: fabric.Object, skipHistory?: boolean) {
    if (obj && this.isSignObject(obj) && this.get(obj.state)) {
      if (!skipHistory) {
        this.history.recordObjectRemoved(this.state, obj)
      }

      this.objects = this.objects.filter(item => item !== obj)
      const objects = this.sign.state.objects.filter(state => !Sign.sameObject(state, obj.state))

      if (obj === this.active) {
        let next: SignObject | undefined = undefined

        if (obj instanceof SignImage) {
          next = this.images.filter(o => !this.state.doubleSided || o.state.side === this.state.side).pop()
        } else if (obj instanceof SignText) {
          next = this.texts.filter(o => !this.state.doubleSided || o.state.side === this.state.side).pop()
        }

        this.sign.setObjectActive(next ? next.state : null)
      }

      if (this.sign.objectRendered(obj)) {
        this.sign.canvas.remove(obj)
      }

      this.sign.updateState({ objects }, undefined, skipHistory)
    }
  }

  clear() {
    this.objects = []
  }

  /**
   * Sync new object state to objects when new sign state has been set in Sign.updateState
   */
  syncFromState(state: SignState) {
    for (const objState of state.objects) {
      let obj = this.get(objState)

      if (obj) {
        obj.state = objState

        if (Sign.isText(objState)) {
          obj = obj as SignText

          const newState = SignText.stateToObject(objState, obj, this.centerX, this.centerY)
          obj.set(newState)

          // Enforce max size for texts on contours
          const limitTextSize = state.shape === "contour" || this.sign.signType === "contour_cut"
          let textPadding = 0

          if (state.shape === "contour" && state.contour) {
            textPadding = contourSize(state.contour) / 0.1
          } else if (this.sign.signType === "contour_cut") {
            textPadding = Calc.mmToPx(10)
          }

          if (limitTextSize) {
            const diffW =
              obj.getScaledWidth() /
              Math.min(obj.getScaledWidth(), Calc.mmToPx(this.sign.fullData.max_size.w) - textPadding)
            const diffH =
              obj.getScaledHeight() /
              Math.min(obj.getScaledHeight(), Calc.mmToPx(this.sign.fullData.max_size.h) - textPadding)
            const diff = Math.max(diffW, diffH)

            if (diff > 1) {
              const desiredSize =
                Math.round((Calc.pxToFontsize((obj.scaleX || 1) * (obj.fontSize || 0)) / diff) * 10) / 10
              const size = correctFontSize(desiredSize, this.sign.data, state, obj.state.font, obj.state.text)

              obj.set({
                fontSize: Calc.fontsizeToPx(size * 1),
                scaleX: 1,
                scaleY: 1,
              })

              obj.state.size = size
            }
          }

          obj.editable = obj.fontFamily !== "Braille (SE 6p)"

          if (objState.size || limitTextSize) {
            obj.scaleStylesFontSizes(this.sign.data)
          }
        } else if (Sign.isImage(objState)) {
          obj = obj as SignImage

          const newState = SignImage.stateToObject(objState, obj, this.centerX, this.centerY)
          obj.set(newState)

          if (objState.color || objState.width || objState.height) {
            obj.updateImage()
          }
        }

        obj.setCoords()
      }
    }

    for (const obj of this.objects) {
      if (!state.objects.map(o => o.identity).includes(obj.state.identity)) {
        this.remove(obj, true)
      }
    }

    if (this.objects.length > 0) {
      this.sign.rerender()
    }
  }

  updateObjectBorder(state: Partial<SignObjectBorder> | null) {
    const obj = this.active

    if (!obj) {
      return
    }

    if (state === null) {
      const newState: Partial<SignObjectState> = { border: undefined }
      this.updateState(obj, newState, true)
    } else {
      const newState: Partial<SignObjectState> = { border: undefined }
      const borderColor =
        this.sign.data.multicolor && this.sign.fullData.default_color ? this.sign.fullData.default_color : "#000000"

      if (!obj?.state.border) {
        newState.border = {
          shape: "rect",
          color: undefined,
          borderColor,
          size: 1,
          thickness: 1,
          ...state,
        }
      } else {
        newState.border = { ...obj.state.border, ...state }
      }

      this.updateState(obj, newState, true)
    }
  }

  updateActive() {
    if (this.active && this.isSignObject(this.active) && this.get(this.active.state)) {
      this.dispatchActiveChanged(this.active.state)
      this.objectPosition(this.active)
      return
    }

    this.dispatchActiveChanged(null)
  }

  resetActiveState() {
    if (this.active) {
      if (this.active instanceof SignImage) {
        const currentState = this.active.state

        const ratio = currentState.ratio || currentState.height / currentState.width
        const size = defaultImageSize(this.sign.state.heightPx, this.sign.state.widthPx, ratio)

        const state: SignImageState = {
          left: 0,
          top: 0,
          zindex: currentState.zindex,
          mirrored: false,
          angle: 0,
          color: "#000000",
          width: size.width,
          height: size.height,
          ratio,
          name: currentState.name,
          id: currentState.id,
          colored: currentState.colored,
          identity: currentState.identity,
        }

        this.updateState(this.active, state, true)
      }
    }
  }

  async updateActiveState(state: Partial<SignObjectState>, fontdata: Fontdata) {
    if (this.active) {
      let draw = true

      if (this.active instanceof SignText) {
        if (
          state.font ||
          Object.prototype.hasOwnProperty.call(state, "bold") ||
          Object.prototype.hasOwnProperty.call(state, "italic")
        ) {
          const font = state.font || this.active.state.font
          const bold = state.bold || this.active.state.bold
          const italic = state.italic || this.active.state.italic

          if (bold || italic) {
            const allowed = Fonts.allowedStyles(font, fontdata)

            if (bold && italic && !allowed.boldItalic) {
              state.bold = false
              state.italic = false
            }

            if (bold && !allowed.bold) {
              state.bold = false
            }

            if (italic && !allowed.italic) {
              state.italic = false
            }
          }

          try {
            await Fonts.loadFont(font, fontdata)
          } catch (e) {
            error(e)
          }
        }

        if (state.text) {
          draw = false

          const orig = state.text
          let text = stripEmoji(orig)

          if (orig !== text) {
            this.onEmojiUsedEvent.dispatchAsync()
          }

          text = stripInvalid(text)

          if (text !== orig) {
            state.text = text
          }
        }

        if (state.size) {
          state.size = +state.size.toFixed(1)
        }

        if (state.height) {
          state.height = +state.height.toFixed(1)
        }
      }

      if (state.angle === -360) {
        state.angle = 0
      }

      this.updateState(this.active, state, draw)
    }
  }

  updateImages() {
    for (const obj of this.objects) {
      if (obj instanceof SignImage) {
        obj.updateImage()
      }
    }
  }

  arrangeObject(state: SignObjectState, direction: "front" | "forward" | "backward") {
    const target = this.get(state)
    let objects = [...this.sign.state.objects].sort((a, b) => a.zindex - b.zindex)

    if (direction === "front") {
      objects = [...objects.filter(obj => !Sign.sameObject(obj, state)), state]
    } else if (direction === "forward" || direction === "backward") {
      const index = objects.indexOf(state)
      const before = []
      const after = []

      for (let i = 0; i < objects.length; i++) {
        const obj = objects[i]

        if (i < index) {
          before.push(obj)
        } else if (i > index) {
          after.push(obj)
        }
      }

      if (direction === "forward") {
        objects = [...before, after.shift(), state, ...after].filter(Util.isPresent)
      } else if (direction === "backward") {
        const move = before.pop()
        objects = [...before, state, move, ...after].filter(Util.isPresent)
      }
    }

    objects = objects.filter(obj => obj)

    const newObjects = [...this.sign.state.objects]

    for (let index = 0; index < objects.length; index++) {
      const element = objects[index]
      const matching = objects.find(obj => obj === element)

      if (matching) {
        matching.zindex = index
      }
    }

    this.sign.updateState({ objects: newObjects }, true)

    if (target === this.active) {
      this.dispatchActiveChanged(target.state)
    }
  }

  restore(objects: SignObjectState[], centerX: number, centerY: number, fontdata: Fontdata): Promise<void> {
    this.objectsToRestore = objects.length

    return new Promise(resolve => {
      if (objects.length === 0) {
        return resolve()
      }

      const loaded = (obj?: SignObject) => {
        if (obj) {
          this.objects.push(obj)
          this.objects = this.sortByStateIndex(this.objects)
          this.sign.updateState({ objects: this.objects.map(o => o.state) }, true)
        }

        this.objectsToRestore -= 1
        if (this.objectsRestored) {
          resolve()
        }
      }

      let firstTextSwapped = false
      const minimaticColor =
        ClientData.minimaticData && (ClientData.minimaticData.textColor || ClientData.minimaticData.contentColor)

      for (const objState of objects) {
        objState.identity = Symbol()

        if (Sign.isText(objState)) {
          if (ClientData.minimaticSign) {
            if (ClientData.minimaticData) {
              if (ClientData.minimaticData.text1 && !firstTextSwapped) {
                objState.text = ClientData.minimaticData.text1
              }

              if (ClientData.minimaticData.text2 && firstTextSwapped) {
                objState.text = ClientData.minimaticData.text2
              }

              if (minimaticColor) {
                objState.color = minimaticColor

                if (objState.border) {
                  objState.border.borderColor = minimaticColor
                }
              }

              if (ClientData.minimaticData.font) {
                objState.font = ClientData.minimaticData.font
              }
            }
          }
          firstTextSwapped = true
          info(`restoring text ${objState.text}`)
          SignText.fromState(this.sign, objState, centerX, centerY, fontdata).then(loaded)
        } else if (Sign.isImage(objState)) {
          if (
            ClientData.minimaticSign &&
            ClientData.minimaticData &&
            (this.sign.data.multicolor || this.sign.data.transparent) &&
            !objState.colored
          ) {
            if (minimaticColor) {
              objState.color = minimaticColor

              if (objState.border) {
                objState.border.borderColor = minimaticColor
              }
            }
          }

          info(`restoring image ${objState.id}`)
          SignImage.fromState(this.sign, objState, centerX, centerY).then(loaded)
        } else {
          error(`Unrecognized object: ${objState}`)
          loaded()
        }
      }
    })
  }

  sortByStateIndex(objects: SignObject[]): SignObject[] {
    return objects.sort((a, b) => (a.state.oindex || 0) - (b.state.oindex || 0))
  }

  cloneFromState(state: SignState, centerX: number, centerY: number): SignObject[] {
    const objects: SignObject[] = []

    for (const objState of state.objects) {
      const clone = this.cloneObject(objState, centerX, centerY)

      if (clone) {
        objects.push(clone)
      }
    }

    return objects.sort((a, b) => a.state.zindex - b.state.zindex)
  }

  cloneObject(objState: SignObjectState, centerX: number, centerY: number) {
    if (Sign.isText(objState)) {
      const obj = SignText.create(objState, centerX, centerY, this.sign.minimatic)
      obj.state = { ...objState, identity: Symbol(), oindex: this.lastOIndex() + 1 }

      if (objState.border) {
        obj.state.border = { ...objState.border }
      }

      return obj
    } else if (Sign.isImage(objState)) {
      const original = this.objects.find(item => Sign.sameObject(item.state, objState)) as SignImage
      const obj = SignImage.create(original.getElement() as HTMLImageElement, objState, centerX, centerY)
      obj.state = { ...objState, identity: Symbol(), oindex: this.lastOIndex() + 1 }

      if (objState.border) {
        obj.state.border = { ...objState.border }
      }

      return obj
    }

    return null
  }

  handleTextChanged(obj: fabric.Object) {
    if (this.isSignObject(obj) && this.get(obj.state)) {
      const target = obj as SignText
      const orig = target.text || ""
      let text = stripEmoji(orig)

      if (target.placeholder.length > 0) {
        text = text.replace(target.placeholder, "")
        target.set("placeholder", "")
      }

      if (orig !== text) {
        target.set("text", text)
        this.onEmojiUsedEvent.dispatchAsync()
      }

      this.updateState(target, { text }, true)
    }
  }

  handleMoving = throttle(
    (obj: fabric.Object): PositionResult | null => {
      if (this.isSignObject(obj)) {
        const result: PositionResult = {
          centerX: false,
          centerY: false,
          guidesX: [],
          guidesY: [],
        }

        const centerDiff = this.state.widthPx * 0.01
        const snapDiff = centerDiff / 2
        const objTop = (obj.top || 0) - obj.getScaledHeight() / 2
        const objBottom = (obj.top || 0) + obj.getScaledHeight() / 2
        const objLeft = (obj.left || 0) - obj.getScaledWidth() / 2
        const objRight = (obj.left || 0) + obj.getScaledWidth() / 2

        // snap to sign center
        const diffLeft = this.centerX - (obj.left || 0)
        const diffTop = this.centerY - (obj.top || 0)

        if (!this.state.widthAuto && diffLeft < centerDiff && diffLeft > -centerDiff) {
          obj.left = this.centerX
          result.centerX = true
        }

        if (!this.state.heightAuto && diffTop < centerDiff && diffTop > -centerDiff) {
          obj.top = this.centerY
          result.centerY = true
        }

        // snap to other objects
        for (const other of this.objects) {
          if (other === obj) {
            continue
          }

          const otherTop = (other.top || 0) - other.getScaledHeight() / 2
          const otherBottom = (other.top || 0) + other.getScaledHeight() / 2
          const otherLeft = (other.left || 0) - other.getScaledWidth() / 2
          const otherRight = (other.left || 0) + other.getScaledWidth() / 2

          if (!result.centerY) {
            if ((other.top || 0) - (obj.top || 0) < snapDiff && (other.top || 0) - (obj.top || 0) > -snapDiff) {
              obj.top = other.top
            }

            if (otherTop - objTop < snapDiff && otherTop - objTop > -snapDiff) {
              obj.top = otherTop + obj.getScaledHeight() / 2
            }

            if (otherBottom - objBottom < snapDiff && otherBottom - objBottom > -snapDiff) {
              obj.top = otherBottom - obj.getScaledHeight() / 2
            }

            if (obj.top === other.top && other.top) {
              result.guidesY.push(other.top)
            }

            if (obj.top === otherTop + obj.getScaledHeight() / 2) {
              result.guidesY.push(otherTop)
            }

            if (obj.top === otherBottom - obj.getScaledHeight() / 2) {
              result.guidesY.push(otherBottom)
            }
          }

          if (!result.centerX) {
            if ((other.left || 0) - (obj.left || 0) < snapDiff && (other.left || 0) - (obj.left || 0) > -snapDiff) {
              obj.left = other.left
            }

            if (otherLeft - objLeft < snapDiff && otherLeft - objLeft > -snapDiff) {
              obj.left = otherLeft + obj.getScaledWidth() / 2
            }

            if (otherRight - objRight < snapDiff && otherRight - objRight > -snapDiff) {
              obj.left = otherRight - obj.getScaledWidth() / 2
            }

            if (obj.left === other.left && other.left) {
              result.guidesX.push(other.left)
            }

            if (obj.left === otherLeft + obj.getScaledWidth() / 2) {
              result.guidesX.push(otherLeft)
            }

            if (obj.left === otherRight - obj.getScaledWidth() / 2) {
              result.guidesX.push(otherRight)
            }
          }
        }

        // stick single object on autosized sign to center
        if (this.state.widthAuto && this.objects.length === 1) {
          obj.left = this.centerX
        }

        if (this.state.heightAuto && this.objects.length === 1) {
          obj.top = this.centerY
        }

        // update object state with current coordinates
        const left = (obj.left || 0) - this.centerX
        const top = (obj.top || 0) - this.centerY

        this.updateState(obj, { left, top })
        return result
      }

      return null
    },
    16,
    { trailing: true }
  )

  handleRotating(obj: fabric.Object) {
    if (this.isSignObject(obj)) {
      const diff = 4
      let angle = obj.angle || 0

      if (angle > 360 - diff || angle < diff) {
        angle = 0
      } else if (angle > 90 - diff && angle < 90 + diff) {
        angle = 90
      } else if (angle > 180 - diff && angle < 180 + diff) {
        angle = 180
      } else if (angle > 270 - diff && angle < 270 + diff) {
        angle = 270
      }

      obj.angle = angle
      this.updateState(obj, { angle })
      this.startRotating()
      this.objectPosition(obj)
    }
  }

  handleModified(obj: fabric.Object) {
    if (this.isSignObject(obj) && this.get(obj.state) && obj instanceof SignText) {
      const scale = obj.scaleX || 1
      const desiredSize = Math.round(Calc.pxToFontsize(scale * (obj.fontSize || 0)) * 10) / 10
      const size = correctFontSize(desiredSize, this.sign.data, this.state, obj.state.font, obj.text)

      obj.set({
        fontSize: Calc.fontsizeToPx(size * 1),
        scaleX: 1,
        scaleY: 1,
      })

      // Set font size on individual character styles
      obj.scaleStylesFontSizes(this.sign.data)
      obj.setCoords()

      this.updateState(obj, { size })
    }

    if (obj === this.active) {
      this.stopRotating()
    }
  }

  handleScaling(obj: fabric.Object) {
    if (this.isSignObject(obj) && this.get(obj.state) && obj instanceof SignImage) {
      const imageW = Math.max((obj.scaleX || 1) * (obj.width || 0), 5) || 5
      const imageH = Math.max((obj.scaleY || 1) * (obj.height || 0), 5) || 5

      const update: Partial<SignImageState> = { width: imageW, height: imageH }

      if (obj.state.mirrored !== obj.flipX) {
        update.mirrored = obj.flipX
      }

      this.updateState(obj, update)
    }
  }

  handleSelected(obj: fabric.Object) {
    if (this.isSignObject(obj) && this.get(obj.state)) {
      this.arrangeObject(obj.state, "front")
      this.objectPosition(obj)
    }
  }

  handleSideChange() {
    if (this.active && this.active.state.side !== this.state.side) {
      this.sign.setObjectActive(null)
    }
  }

  resetPositions(centerX: number, centerY: number) {
    for (const obj of this.objects) {
      obj.left = centerX + obj.state.left
      obj.top = centerY + obj.state.top
      obj.setCoords()
    }
  }

  keepWithinBounds(obj: SignObject) {
    const contour = this.state.shape === "contour"
    let formTop = this.centerY - this.state.heightPx / 2
    let formLeft = this.centerX - this.state.widthPx / 2
    let formBottom = this.centerY + this.state.heightPx / 2
    let formRight = this.centerX + this.state.widthPx / 2

    if (contour && this.state.contour) {
      const maxW = Calc.mmToPx(this.sign.fullData.max_size.w) / 2 - contourSize(this.state.contour) / 0.1
      const maxH = Calc.mmToPx(this.sign.fullData.max_size.h) / 2 - contourSize(this.state.contour) / 0.1
      formTop = this.centerY - maxH + obj.getScaledHeight() / 2
      formBottom = this.centerY + maxH - obj.getScaledHeight() / 2
      formLeft = this.centerX - maxW + obj.getScaledWidth() / 2
      formRight = this.centerX + maxW - obj.getScaledWidth() / 2
    }

    if (obj.top && obj.top < formTop) {
      obj.top = formTop
    }
    if (obj.top && obj.top > formBottom) {
      obj.top = formBottom
    }
    if (obj.left && obj.left < formLeft) {
      obj.left = formLeft
    }
    if (obj.left && obj.left > formRight) {
      obj.left = formRight
    }

    obj.setCoords()
    obj.state.left = (obj.left || 0) - this.centerX
    obj.state.top = (obj.top || 0) - this.centerY

    if (obj === this.active) {
      this.dispatchActiveChanged(obj.state)
      this.objectPosition(obj)
    }
  }

  autoSize(): SignState {
    if ((!this.state.widthAuto && !this.state.heightAuto) || !this.objectsRestored) {
      return this.state
    }

    const newState = { ...this.state }
    let padding = 25

    if (this.state.shape === "contour" && this.state.contour) {
      if (this.state.width > 700 || this.state.height > 700) {
        padding = 250
      } else if (this.state.width > 300 || this.state.height > 300) {
        padding = 170
      } else {
        padding = 65
      }
    }

    if (this.sign.signType === "contour_cut") {
      padding = 0
    }

    const adjustment = this.autoSizeAdjustment(this.state, padding)

    for (const obj of this.objects) {
      if (newState.widthAuto && adjustment.width !== 0) {
        obj.left = (obj.left || 0) + adjustment.left
        obj.state.left = obj.left - this.centerX
      }
      if (newState.heightAuto && adjustment.height !== 0) {
        obj.top = (obj.top || 0) + adjustment.top
        obj.state.top = obj.top - this.centerY
      }

      obj.setCoords()
    }

    if (newState.widthAuto) {
      if (adjustment.width !== 0) {
        newState.width = Math.round(Calc.pxToMm(newState.widthPx + adjustment.width))
        newState.width = Math.max(Math.min(this.sign.data.max_size.w, newState.width), this.sign.data.min_size.w)
      } else if (this.objects.length === 0) {
        newState.width = this.sign.data.min_size.w
      }
    }

    if (newState.heightAuto) {
      if (adjustment.height !== 0) {
        newState.height = Math.round(Calc.pxToMm(newState.heightPx + adjustment.height))
        newState.height = Math.max(Math.min(this.sign.data.max_size.h, newState.height), this.sign.data.min_size.h)
      } else if (this.objects.length === 0) {
        newState.height = this.sign.data.min_size.h
      }
    }

    return newState
  }

  textContent() {
    const textStates: SignTextState[] = []

    for (const obj of this.objects) {
      if (Sign.isText(obj.state)) {
        if (obj.state.side && obj.state.side > 0 && !this.state.doubleSided) {
          continue
        }

        textStates.push(obj.state)
      }
    }

    return textStates
      .map(obj => obj.text)
      .join(" ")
      .replace(/\n|\r|\r\n/, " ")
      .trim()
  }

  textDescription() {
    const text = this.textContent()

    if (text.length === 0) {
      return "..."
    } else if (text.length > 15) {
      return text.substring(0, 15) + "..."
    }

    return text
  }

  imageParams() {
    const params = []

    for (const obj of this.objects) {
      if (obj instanceof SignImage) {
        const state = obj.state

        if (state.side && state.side > 0 && !this.state.doubleSided) {
          continue
        }

        params.push({
          name: state.id,
          bw: !state.colored,
          w: Calc.pxToDPI(25.4, state.width),
          h: Calc.pxToDPI(25.4, state.height),
          border: "none",
        })
      }
    }

    return params
  }

  // Import text content into current template
  positionTexts(contents: string[]) {
    contents = contents.map(line => line.toString())

    const texts = this.texts.map(o => o.state)
    const form = this.sign.currentBase()

    if (!form) {
      throw new Error("Can't find base form")
    }

    const signHeight = form.getBoundingRect(true, true).height
    const count = texts.length
    const empty = count === 0 || (count === 1 && texts[0].text === "")
    const first = texts[0]
    const position = []

    // if there is an existing text object all created objects get the same style
    const style =
      count > 0
        ? {
            font: first.font,
            size: first.size,
          }
        : {}

    if (!empty && count > 0 && count >= contents.length) {
      // all target objects exist - replace texts and remove extra objects

      for (let index = 0; index < count; index++) {
        const text = contents[index]
        const obj = this.get(texts[index]) as SignText

        this.setImportedText(obj, { text: text || "" })
        this.scaleImportedToBounds(form, obj)
      }
    } else if (empty) {
      // no existing objects - space texts evenly vertically

      // placeholder text object, remove
      if (count === 1) {
        this.remove(this.get(texts[0]))
      }

      let height = 0

      for (const text of contents) {
        const obj = this.createText({ text, ...style })
        this.add(obj)

        this.scaleImportedToBounds(form, obj)
        height += this.objHeight(obj)
        position.push(obj)
      }

      // if there is one object it gets centered by default
      if (contents.length > 1) {
        const gap = (signHeight - height) / (contents.length + 1)
        let top = -(signHeight / 2 - gap)

        for (const obj of position) {
          this.updateState(obj, { top: top + this.objHeight(obj) / 2 })
          top += this.objHeight(obj) + gap
        }
      }
    } else {
      // some target objects don't exist - replace text on existing and lay the rest from bottom up in reverse

      let top = null

      for (let index = 0; index < count; index++) {
        const text = contents.shift()
        const obj = this.get(texts[index]) as SignText

        this.setImportedText(obj, { text, ...style })
        this.scaleImportedToBounds(form, obj)
      }

      for (const text of contents.reverse()) {
        const obj = this.createText({ text, ...style })
        this.add(obj)

        this.scaleImportedToBounds(form, obj)

        if (!top) {
          top = signHeight / 2 - obj.getScaledHeight()
        }

        this.updateState(obj, { top: top + this.objHeight(obj) / 2 })

        obj.set("top", top)
        top -= obj.getScaledHeight()
      }
    }
  }

  resizeImages(width: number, height: number) {
    for (const img of this.images) {
      this.updateState(img, { width, height })
    }
  }

  exitEditing() {
    if (this.active && this.active instanceof SignText && this.active.isEditing) {
      this.active.exitEditing()
      this.sign.rerender()
    }
  }

  enableSelect() {
    for (const obj of this.objects) {
      obj.selectable = true
    }
  }

  disableSelect() {
    for (const obj of this.objects) {
      obj.selectable = false
    }
  }

  isSignObject(obj: fabric.Object): obj is SignObject {
    return obj instanceof SignImage || obj instanceof SignText
  }

  private startRotating() {
    this.onRotatingStartedEvent.dispatchAsync()
  }

  private stopRotating() {
    this.onRotatingStoppedEvent.dispatchAsync()
  }

  objectPosition(obj: SignObject) {
    if (obj.oCoords) {
      const objectCenter = (obj.oCoords.tl.x + obj.oCoords.br.x) / 2
      const lowestY = Math.max(obj.oCoords.bl.y, obj.oCoords.br.y, obj.oCoords.tl.y, obj.oCoords.tr.y)

      this.onObjectPositionChangedEvent.dispatchAsync([lowestY, objectCenter])
    }
  }

  updateState(target: SignObject, state: Partial<SignObjectState>, draw = false) {
    const newState = { objects: this.state.objects }

    for (const objState of newState.objects) {
      const index = newState.objects.indexOf(objState)

      if (Sign.sameObject(objState, target.state)) {
        this.history.recordObjectPast(this.state, target, state)

        for (const key in state) {
          if (Object.prototype.hasOwnProperty.call(state, key)) {
            target.state[key] = state[key]
          }
        }
        newState.objects[index] = target.state
      }
    }

    this.sign.updateState(newState, draw, true)

    if (target === this.active) {
      this.dispatchActiveChanged(target.state)
    }
  }

  // Update text object from imported text content while preserving alignment position
  private setImportedText(target: SignText, state: Partial<SignTextState>) {
    const realignment = target.state.justify
    const originalWidth = target.getScaledWidth()

    this.updateState(target, state, true)

    if (realignment === "left") {
      const widthDiff = target.getScaledWidth() - originalWidth
      const left = target.state.left + widthDiff / 2
      this.updateState(target, { left })
    } else if (realignment === "right") {
      const widthDiff = target.getScaledWidth() - originalWidth
      const left = target.state.left - widthDiff / 2
      this.updateState(target, { left })
    }
  }

  // Scale imported text content to fill maximum space possible
  private scaleImportedToBounds(form: fabric.Object, obj: SignText) {
    const formRect = form.getBoundingRect(true, true)
    const rect = obj.getBoundingRect(true, true)
    let extraX = 0
    let extraY = 0

    formRect.left -= (obj.width || 0) / 2
    formRect.width += obj.width || 0
    formRect.top -= (obj.height || 0) / 2
    formRect.height += obj.height || 0

    if (rect.left < formRect.left) {
      extraX = Math.max(extraX, formRect.left - rect.left)
    }
    if (rect.left + rect.width > formRect.left + formRect.width) {
      extraX = Math.max(extraX, rect.left + rect.width - (formRect.left + formRect.width))
    }
    if (rect.top < formRect.top) {
      extraY = Math.max(extraY, formRect.top - rect.top)
    }
    if (rect.top + rect.width > formRect.height + formRect.height) {
      extraY = Math.max(extraY, rect.top + rect.height - (formRect.top + formRect.height))
    }

    if (extraX > 0 || extraY > 0) {
      const scaleX = (rect.width - extraX * 2) / rect.width
      const scaleY = (rect.height - extraY * 2) / rect.height
      const scale = Math.min(scaleX, scaleY)

      this.updateState(obj, { size: obj.state.size * scale })
    }
  }

  private objHeight(obj: fabric.Object) {
    return obj.getBoundingRect(true, true).height
  }

  private createText(initialState?: Partial<SignTextState>): SignText {
    const color = (this.sign.data.multicolor ? this.sign.fullData.default_color : this.state.contentColor) || "#000000"
    const zindex = this.lastZIndex() + 1
    const previous = this.texts.length > 0 ? this.texts[this.texts.length - 1] : null
    const oindex = this.lastOIndex() + 1

    const state: SignTextState = {
      text: "",
      left: 0,
      top: 0,
      size: this.defaultFontSize(),
      font: previous ? previous.state.font : this.sign.signType === "tactile_sign" ? "WorkSans" : "Arial",
      italic: false,
      bold: false,
      underlined: false,
      justify: "left",
      oindex,
      zindex,
      mirrored: false,
      angle: 0,
      color: previous ? previous.state.color : color,
      side: this.state.side,
      identity: Symbol(),
      width: 0,
      ...initialState,
    }

    const newObj = SignText.create(state, this.centerX, this.centerY)
    newObj.state = state

    // textObj should have opacity to look like a placeholder
    if (newObj.isPlaceholder) {
      newObj.opacity = 0.5
      newObj.state.text = newObj.placeholder
    }

    return newObj
  }

  private async createImage(initialState?: Partial<SignImageState>): Promise<SignObject> {
    const color = (this.sign.data.multicolor ? this.sign.fullData.default_color : this.state.contentColor) || "#000000"
    const zindex = this.lastZIndex() + 1
    const oindex = this.lastOIndex() + 1

    const state: SignImageState = {
      left: 0,
      top: 0,
      oindex,
      zindex,
      mirrored: false,
      angle: 0,
      color,
      width: 100,
      height: 100,
      ratio: initialState?.width && initialState.height ? initialState.height / initialState.width : 1,
      name: "Image",
      id: 1,
      colored: this.sign.data.multicolor,
      side: this.state.side,
      identity: Symbol(),
      ...initialState,
    }

    const url = SignImage.imageUrl(state, this.sign.defaultScale, this.sign.data.multicolor)
    const img = await loadImage(url, "Anonymous")

    const newObj = SignImage.create(img, state, this.centerX, this.centerY)
    newObj.state = state

    return newObj
  }

  private autoSizeAdjustment(state: SignState, padding: number) {
    const shape = state.shape
    const diff = { left: 0, top: 0, width: 0, height: 0 }
    const max = { left: 0, top: 0, right: 0, bottom: 0 }
    let arrowDiff = 0
    let { widthPx, heightPx } = state

    widthPx -= padding
    heightPx -= padding

    if (this.sign.signType === "contour_cut") {
      widthPx -= Calc.mmToPx(10) * 2
      heightPx -= Calc.mmToPx(10) * 2
    }

    const bounds = {
      left: this.centerX - widthPx / 2,
      top: this.centerY - heightPx / 2,
      width: widthPx,
      height: heightPx,
    }

    const originalBoundsHeight = bounds.height

    if (shape === "arrow_left" || shape === "arrow_right") {
      arrowDiff = bounds.height / 5
      bounds.top += arrowDiff
      bounds.height -= arrowDiff

      if (shape === "arrow_left") {
        bounds.left += (bounds.width * 1) / 5 / 3
      } else {
        bounds.left -= (bounds.width * 1) / 5 / 3
      }
    }

    if (this.objects.length > 0) {
      const first = this.getBounds(this.objects[0])
      max.left = first.left
      max.top = first.top

      for (const obj of this.objects) {
        const objBounds = this.getBounds(obj.objectBorderForm() || obj)

        max.left = Math.min(max.left, objBounds.left)
        max.top = Math.min(max.top, objBounds.top)
        max.right = Math.max(max.right, objBounds.left + objBounds.width)
        max.bottom = Math.max(max.bottom, objBounds.top + objBounds.height)
      }

      const diffLeft = Math.round(-(max.left - bounds.left))
      diff.width += diffLeft
      diff.left += diffLeft / 2

      const diffRight = Math.round(-(bounds.left + bounds.width - max.right))
      diff.width += diffRight
      diff.left -= diffRight / 2

      const diffTop = Math.round(-(max.top - bounds.top))
      diff.height += diffTop
      diff.top += diffTop / 2

      const diffBottom = Math.round(-(bounds.top + bounds.height - max.bottom))
      diff.height += diffBottom
      diff.top -= diffBottom / 2

      diff.width += bounds.width - widthPx
      diff.height += originalBoundsHeight - heightPx

      if (shape === "arrow_left" || shape === "arrow_right") {
        diff.width += arrowDiff
        diff.height += arrowDiff
        diff.top -= arrowDiff / 2
      }
    }

    return diff
  }

  private dispatchActiveChanged(state: SignObjectState | null) {
    let info: ActiveObjectInfo | null = null

    if (state) {
      const obj = this.get(state)

      if (obj) {
        const width = obj.getScaledWidth()
        const height = obj.getScaledHeight()
        const angle = obj.angle || 0

        const base = this.sign.currentBase()
        const centerX = this.sign.centerX
        const centerY = this.sign.centerY
        const left = Math.round((obj.left || 0) - width / 2 - (centerX - (base?.width || 0) / 2))
        const right = Math.round(centerX + (base?.width || 0) / 2 - ((obj.left || 0) + width / 2))
        const top = Math.round((obj.top || 0) - height / 2 - (centerY - (base?.height || 0) / 2))
        const bottom = Math.round(centerY + (base?.height || 0) / 2 - ((obj.top || 0) + height / 2))

        info = {
          width,
          height,
          left,
          right,
          top,
          bottom,
          angle,
        }

        if (obj instanceof SignText) {
          this.onGlyphsPresentEvent.dispatchAsync(obj.hasGlyphs(state.text))
        }
      }
    }

    this.onActiveChangedEvent.dispatch(state)
    this.onActiveInfoChangedEvent.dispatchAsync(info)
  }

  private getBounds(obj: fabric.Object) {
    const width = obj.getScaledWidth()
    const height = obj.getScaledHeight()

    return {
      left: (obj.left || 0) - width / 2,
      top: (obj.top || 0) - height / 2,
      width,
      height,
    }
  }

  private defaultFontSize() {
    let textSize = Math.round(this.state.height * 0.12)

    if (Util.metaNameSign(this.sign.signType || "")) {
      textSize = 5
    } else if (this.sign.signType === "label_plates" && this.state.badgeName) {
      textSize = 10
    } else if (
      ["plastic_sign", "wood_sign", "acryl_sign", "vinyl_text", "acryl_alu_sign"].includes(this.sign.signType || "")
    ) {
      textSize = 12
    } else if (this.sign.signType === "rollup") {
      textSize = env.locale === "nl-NL" ? 65 : 80
    } else if (this.sign.signType === "tactile_sign") {
      textSize = 17
    }

    return correctFontSize(textSize, this.sign.data, this.state)
  }

  private lastZIndex() {
    return this.objects.length > 0 ? Math.max(...this.objects.map(obj => obj.state.zindex)) : 0
  }

  private lastOIndex() {
    return this.objects.length > 0 ? Math.max(...this.objects.map(obj => obj.state.oindex || 0)) : 0
  }
}
